/*
 * The posts calendar (mkPostsHTML in 15-marketing.js): a month of Facebook, Instagram and Google posts with the holidays
 * worth a post on their days, and the month's holidays below with an idea for the brand's trade. Tapping a day or a
 * holiday starts a post for that day; tapping a post opens it. Drafts with a day show faded on it.
 */
import type { ReactNode } from "react";
import { holidaysIn, isoToWall, monthDays, shiftMonth, type HolidayOn, type SocialPostRow } from "@steward/shared";
import { useSession } from "../app/session";
import { dayDate, monthName, shortDate, todayIn } from "../components/format";
import { Button, Card, IconButton } from "../components/parts";

const WEEK = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const STATUS_WORDS: Record<SocialPostRow["status"], string> = {
  draft: "Draft",
  scheduled: "Scheduled",
  held: "Did not go out",
  posted: "Posted",
};

export function PostsCalendar({
  posts,
  month,
  onMonth,
  onStart,
  onOpen,
  footer,
}: {
  posts: SocialPostRow[];
  month: string;
  onMonth: (m: string) => void;
  /** Start a post on a day, with the holiday's idea when it came from one. Left out for those who may only look. */
  onStart?: ((day: string, idea: string) => void) | undefined;
  onOpen?: ((p: SocialPostRow) => void) | undefined;
  footer?: ReactNode;
}) {
  const { company } = useSession();
  const today = todayIn(company.timezone);
  const when = (iso: string) => isoToWall(iso, company.timezone);
  const timeOf = (iso: string) =>
    new Date(iso)
      .toLocaleTimeString("en-US", { timeZone: company.timezone, hour: "numeric", minute: "2-digit" })
      .replace(":00", "");
  const byDay = new Map<string, SocialPostRow[]>();
  for (const p of posts) {
    if (!p.scheduledFor) continue;
    const d = when(p.scheduledFor).day;
    byDay.set(d, [...(byDay.get(d) ?? []), p]);
  }
  const holidays = holidaysIn(month, company.trade);
  const holOn = new Map<string, HolidayOn[]>();
  for (const h of holidays) holOn.set(h.day, [...(holOn.get(h.day) ?? []), h]);
  return (
    <Card
      title={
        <span className="row tight">
          <IconButton icon="chevronLeft" label="Month before" onClick={() => onMonth(shiftMonth(month, -1))} />
          <span className="mk-cal-month">{monthName(`${month}-01`)}</span>
          <IconButton icon="chevronRight" label="Month after" onClick={() => onMonth(shiftMonth(month, 1))} />
        </span>
      }
      action={
        month === today.slice(0, 7) ? null : (
          <Button type="tertiary" size="small" onClick={() => onMonth(today.slice(0, 7))}>
            This month
          </Button>
        )
      }
    >
      <div className="stack">
        <div className="mk-cal" role="grid" aria-label="Posts calendar">
          {WEEK.map((w) => (
            <span key={w} className="mk-cal-dow" role="columnheader">
              {w}
            </span>
          ))}
          {monthDays(month).map((d) => {
            const list = (byDay.get(d) ?? []).sort((a, b) => (a.scheduledFor ?? "").localeCompare(b.scheduledFor ?? ""));
            const out = d.slice(0, 7) !== month;
            return (
              <div key={d} role="gridcell" className={`mk-cal-day${out ? " out" : ""}${d === today ? " today" : ""}`}>
                {onStart && d >= today ? (
                  <button
                    type="button"
                    className="mk-cal-num"
                    onClick={() => onStart(d, (holOn.get(d) ?? []).map((h) => h.idea).join(" "))}
                    aria-label={`New post on ${dayDate(d)}`}
                  >
                    {Number(d.slice(8, 10))}
                  </button>
                ) : (
                  <span className="mk-cal-num">{Number(d.slice(8, 10))}</span>
                )}
                {(holOn.get(d) ?? []).map((h) =>
                  onStart && d >= today ? (
                    <button key={h.key} type="button" className="mk-cal-hol" title={h.idea} onClick={() => onStart(d, h.idea)}>
                      {h.name}
                    </button>
                  ) : (
                    <span key={h.key} className="mk-cal-hol" title={h.idea}>
                      {h.name}
                    </span>
                  ),
                )}
                {list.map((p) => {
                  const words = `${STATUS_WORDS[p.status]}. ${p.body.slice(0, 200)}`;
                  const inner = (
                    <>
                      <b>{timeOf(p.scheduledFor!)}</b> {p.body.slice(0, 40)}
                    </>
                  );
                  return onOpen ? (
                    <button
                      key={p.id}
                      type="button"
                      className={`mk-cal-post ${p.status}`}
                      title={words}
                      onClick={() => onOpen(p)}
                    >
                      {inner}
                    </button>
                  ) : (
                    <span key={p.id} className={`mk-cal-post ${p.status}`} title={words}>
                      {inner}
                    </span>
                  );
                })}
              </div>
            );
          })}
        </div>
        <p className="muted small">Scheduled posts go out at their time. Tap a day to start a post, or a post to open it.</p>
        {footer}
        {holidays.length ? (
          <div className="stack">
            <b>Holidays this month</b>
            <p className="muted small">Ideas to post about. Post a few days before, so people have time to book.</p>
            <ul className="auto-list">
              {holidays.map((h) => (
                <li key={h.key} className="setting-row">
                  <span className="what">
                    <b>
                      {h.name} <span className="muted small">{shortDate(h.day)}</span>
                    </b>
                    <span>{h.idea}</span>
                  </span>
                  {onStart ? (
                    <Button type="secondary" size="small" onClick={() => onStart(h.day < today ? today : h.day, h.idea)}>
                      Start a post
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {posts.some((p) => p.scheduledFor && when(p.scheduledFor).day.startsWith(month)) ? null : (
          <p className="muted small">{`No posts in ${monthName(`${month}-01`)} yet.`}</p>
        )}
      </div>
    </Card>
  );
}
